// M11 - reviResourceAPI
//
// Cliente HTTP para el modulo "Formación REVI" del panel admin.
// Consume los endpoints existentes /api/v1/revi-resource (ya usados por
// el portal de terapeuta) sin tocar el backend.
//
// Recursos: { _id, title, description, type, url, file, image, active,
// created_at }. Type puede ser 'video' | 'pdf' | 'info'.

import { ENV } from "../Utils";

const ROUTE = "revi-resource";

function authHeaders(token, extra = {}) {
  return { Authorization: `Bearer ${token}`, ...extra };
}

async function parseResponse(response) {
  let body = null;
  try {
    body = await response.json();
  } catch (e) {
    body = null;
  }
  if (!response.ok) {
    const err = new Error(body?.message || `HTTP ${response.status}`);
    err.status = response.status;
    err.body = body;
    throw err;
  }
  return body;
}

// El backend espera multipart cuando hay archivos. Aceptamos un objeto
// plano con campos string + 2 posibles archivos (image, document) y
// construimos el FormData. Si NO hay archivos, igualmente mandamos
// multipart para mantener un solo code path (multer lo acepta).
function buildFormData(data) {
  const fd = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    // Saltamos archivos vacios para no enviar valores vacios.
    if (key === "image" || key === "document") {
      if (value instanceof File || value instanceof Blob) {
        fd.append(key, value);
      }
      return;
    }
    fd.append(key, value);
  });
  return fd;
}

export class ReviResourceAPI {
  baseApi = ENV.BASE_API;

  // Lista paginada. type opcional ('video' | 'pdf' | 'info'). search libre.
  // audience opcional ('therapist' | 'institution'): sin ella se devuelve todo,
  // que es lo que necesita el panel admin para gestionar.
  async list(token, { page = 1, limit = 12, type, search, audience } = {}) {
    const qs = new URLSearchParams();
    qs.set("page", String(page));
    qs.set("limit", String(limit));
    if (type) qs.set("type", type);
    if (search) qs.set("search", search);
    if (audience) qs.set("audience", audience);
    const url = `${this.baseApi}/${ROUTE}?${qs.toString()}`;
    const response = await fetch(url, { headers: authHeaders(token) });
    return parseResponse(response);
  }

  async getById(token, id) {
    const url = `${this.baseApi}/${ROUTE}/${id}`;
    const response = await fetch(url, { headers: authHeaders(token) });
    return parseResponse(response);
  }

  // payload = { title, description, type, url?, image?: File, document?: File }
  // El backend deduce que el video lleva url externa y el pdf lleva File.
  async create(token, payload) {
    const url = `${this.baseApi}/${ROUTE}`;
    const response = await fetch(url, {
      method: "POST",
      headers: authHeaders(token),
      body: buildFormData(payload),
    });
    return parseResponse(response);
  }

  // payload puede incluir solo los campos a actualizar. Para sustituir el
  // PDF basta con incluir un nuevo File en 'document'; el controller borra
  // el anterior de S3 automaticamente.
  async update(token, id, payload) {
    const url = `${this.baseApi}/${ROUTE}/${id}`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: authHeaders(token),
      body: buildFormData(payload),
    });
    return parseResponse(response);
  }

  async remove(token, id) {
    const url = `${this.baseApi}/${ROUTE}/${id}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: authHeaders(token),
    });
    return parseResponse(response);
  }
}
